import { getFinalScoreColumns, getScoreboard } from '@/feature/bets-sbk/utils/utils';
import { formatSportPeriodDetails } from '@/utils/format-sport-period-details/format-sport-period-details';
import { getTeamOrder } from '@/utils/sport-utils';

import { ScoreBoardGridProps, ScoreboardEvent, StoreCacheSlice } from '../types';
import { buildTeamInfo, cacheToEventDetails, toPeriodScore } from './build-team-info';

/**
 * Baseball builder — innings-driven breakdown plus the R/H final columns from getFinalScoreColumns.
 * Active inning is read from sport_specific.current_inning directly (baseball does not use period_name).
 */
export const buildBaseballScoreboardProps = (
    event: ScoreboardEvent,
    cache: StoreCacheSlice
): Omit<ScoreBoardGridProps, 'variant' | 'matchDetails'> | undefined => {
    const { sportSpecific, eventState } = cache;

    const [firstSide, secondSide] = getTeamOrder(event.sport.name);
    const { team1, team2 } = buildTeamInfo(event, firstSide, secondSide);

    if (eventState === 'pregame') {
        return { team1, team2, breakdown: [], eventState };
    }

    const eventDetails = cacheToEventDetails(event, cache);
    const grid = getScoreboard(event.sport, event.league, eventDetails);
    const finals = getFinalScoreColumns(eventDetails, event.sport);
    const isLive = eventState === 'live';
    const currentInning = sportSpecific?.current_inning;
    const activePeriodIndex =
        isLive && currentInning !== undefined ? grid.findIndex(p => p.periodNumber === String(currentInning)) : -1;
    // e.g. "2 Outs" — only present mid-inning.
    const periodDetails = isLive ? formatSportPeriodDetails(event, sportSpecific) || undefined : undefined;

    return {
        team1,
        team2,
        breakdown: grid.map(p => toPeriodScore(p, firstSide)),
        finalScoreColumns: finals.map(p => toPeriodScore(p, firstSide)),
        activePeriodIndex: activePeriodIndex >= 0 ? activePeriodIndex : undefined,
        periodDetails,
        eventState,
    };
};
